import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Plus, Trash2, Save, FileDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { exportToPDF } from '../utils/exportUtils';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

interface ComparisonSet {
  id: string;
  name: string;
  properties: any[];
  created_at: string;
}

const ComparisonPage: React.FC = () => {
  const navigate = useNavigate();
  const [comparisons, setComparisons] = useState<ComparisonSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [newSetName, setNewSetName] = useState('');

  useEffect(() => {
    loadComparisons();
  }, []);

  const loadComparisons = async () => {
    try {
      const { data, error } = await supabase
        .from('property_comparisons')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setComparisons(data || []);
    } catch (error) {
      console.error('Error loading comparisons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSet = async () => {
    try {
      if (!newSetName) throw new Error('Please enter a name for the comparison set');
      
      const { data, error } = await supabase
        .from('property_comparisons')
        .insert([
          {
            name: newSetName,
            properties: selectedProperties
          }
        ])
        .select();

      if (error) throw error;
      
      setComparisons([...(data || []), ...comparisons]);
      setNewSetName('');
      setSelectedProperties([]);
    } catch (error) {
      console.error('Error creating comparison set:', error);
    }
  };

  const handleDeleteSet = async (id: string) => {
    try {
      const { error } = await supabase
        .from('property_comparisons')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setComparisons(comparisons.filter(set => set.id !== id));
    } catch (error) {
      console.error('Error deleting comparison set:', error);
    }
  };

  const handleExport = (comparison: ComparisonSet) => {
    const data = comparison.properties.map(property => ({
      Location: property.location,
      'Business Type': property.businessType,
      Score: property.score,
      'Market Size': property.marketSize,
      Competition: property.competition
    }));

    exportToPDF(data, `Comparison - ${comparison.name}`);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-6">Property Comparisons</h2>

        <div className="space-y-4">
          <div className="flex gap-4">
            <input
              type="text"
              value={newSetName}
              onChange={(e) => setNewSetName(e.target.value)}
              placeholder="New comparison set name"
              className="flex-1 p-2 bg-card border border-border rounded-lg"
            />
            <button
              onClick={handleCreateSet}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center"
            >
              <Plus size={20} className="mr-2" />
              Create Set
            </button>
          </div>

          {comparisons.map(comparison => (
            <div
              key={comparison.id}
              className="border border-border rounded-lg p-4 space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{comparison.name}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleExport(comparison)}
                    className="text-secondary hover:text-secondary/90"
                  >
                    <FileDown size={20} />
                  </button>
                  <button
                    onClick={() => handleDeleteSet(comparison.id)}
                    className="text-destructive hover:text-destructive/90"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {comparison.properties.map((property: any, index: number) => (
                  <div
                    key={index}
                    className="bg-card/30 p-4 rounded-lg"
                  >
                    <h4 className="font-medium">{property.location}</h4>
                    <p className="text-sm text-muted-foreground">
                      {property.businessType}
                    </p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p>Score: {property.score}</p>
                      <p>Market Size: {property.marketSize}</p>
                      <p>Competition: {property.competition}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComparisonPage;